import { useMemo } from 'react';
import { Content, Page, Header, TableColumn } from '@backstage/core-components';
import {
  CatalogFilterLayout,
  EntityLifecyclePicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntityProcessingStatusPicker,
  EntityRefLink,
  EntityRefLinks,
  EntityTagPicker,
  EntityTypePicker,
  getEntityRelations,
  useEntityList,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import {
  CatalogTable,
  CatalogTableRow,
  DefaultCatalogPageProps,
} from '@backstage/plugin-catalog';
import { HeaderWorldClock, ClockConfig } from '@backstage/plugin-home';
import { CompoundEntityRef, Entity } from '@backstage/catalog-model';

import { WarningRelationshipsColumn } from './columns/WarningRelationshipsColumn';
import { MaturityRankWidget } from '@internal/plugin-maturity';
import { MedidataKindPicker } from '@internal/plugin-medidata-catalog-react';

const clockConfigs: ClockConfig[] = [
  {
    label: 'UTC',
    timeZone: 'UTC',
  },
  {
    label: 'San Francisco',
    timeZone: 'America/Los_Angeles',
  },
  {
    label: 'Houston',
    timeZone: 'America/Chicago',
  },
  {
    label: 'New York',
    timeZone: 'America/New_York',
  },
  {
    label: 'London',
    timeZone: 'Europe/London',
  },
  {
    label: 'India',
    timeZone: 'Asia/Kolkata',
  },
  {
    label: 'Japan',
    timeZone: 'Asia/Tokyo',
  },
];

export const apiColumns: TableColumn<CatalogTableRow>[] = [
  CatalogTable.columns.createNameColumn(),
  {
    title: 'Team(s)',
    field: 'resolved.ownedByRelationsTitle',
    render: ({ resolved }) => (
      <EntityRefLinks
        entityRefs={resolved.ownedByRelations}
        defaultKind="group"
      />
    ),
  },
  {
    title: 'Product',
    field: 'resolved.partOfSystemRelationTitle',
    render: ({ resolved }) => (
      <EntityRefLinks
        entityRefs={resolved.partOfSystemRelations}
        defaultKind="system"
      />
    ),
  },
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createSpecLifecycleColumn(),
];

// Copying https://github.com/backstage/backstage/blob/master/plugins/catalog/src/components/CatalogTable/CatalogTable.tsx#L66
// But with our own customizations
export const CatalogTableWrapper = () => {
  const { filters } = useEntityList();

  const columns = useMemo(() => {
    switch (filters.kind?.value) {
      case 'user':
        return [
          CatalogTable.columns.createNameColumn(),
          {
            title: 'Title',
            field: 'entity.metadata.description',
          },
          {
            title: 'Team(s)',
            field: 'entity.relations',
            render: ({ entity }) => (
              <EntityRefLinks
                entityRefs={getEntityRelations(entity, 'memberOf')}
                defaultKind="group"
              />
            ),
          },
          /** Allows user entities to be searched by an actual name
           *  Has to be hidden because we already display name column
           */
          {
            title: 'Name',
            field: 'entity.metadata.title',
            searchable: true,
            hidden: true,
          },
        ];
      case 'group':
        return [
          CatalogTable.columns.createNameColumn(),
          {
            title: 'Maturity',
            field: 'entity.metadata.not_used',
            render: ({ entity }: Readonly<{ entity: Entity }>) => (
              <MaturityRankWidget entity={entity} chip />
            ),
          },
          {
            title: 'Unknown members',
            field: 'entity.metadata.unknown',
            render: ({ entity }: Readonly<{ entity: Entity }>) => (
              <WarningRelationshipsColumn entity={entity} />
            ),
            hidden: true,
          },
        ];
      case 'domain':
        return [
          CatalogTable.columns.createNameColumn(),
          {
            title: 'Organization',
            field: 'resolved.ownedByRelationsTitle',
            render: ({ resolved }) => (
              <EntityRefLinks
                entityRefs={resolved.ownedByRelations}
                defaultKind="group"
              />
            ),
          },
          {
            title: 'Maturity',
            field: 'entity.metadata.not_used',
            render: ({ entity }: Readonly<{ entity: Entity }>) => (
              <MaturityRankWidget entity={entity} chip />
            ),
          },
        ];
      case 'system':
        return [
          CatalogTable.columns.createNameColumn(),
          {
            title: 'Product Family',
            field: 'entity.spec.domain',
            render: ({ entity }) => (
              <EntityRefLinks
                entityRefs={getEntityRelations(entity, 'partOf')}
                defaultKind="domain"
              />
            ),
          },
          {
            title: 'Maturity',
            field: 'entity.metadata.not_used',
            render: ({ entity }: Readonly<{ entity: Entity }>) => (
              <MaturityRankWidget entity={entity} chip />
            ),
          },
        ];
      case 'api':
        return apiColumns;
      default: // component
        return [
          CatalogTable.columns.createNameColumn(),
          {
            title: 'Team(s)',
            field: 'resolved.ownedByRelationsTitle',
            render: ({ resolved }) => (
              <EntityRefLinks
                entityRefs={resolved.ownedByRelations}
                defaultKind="group"
              />
            ),
          },
          {
            title: 'Product',
            field: 'resolved.partOfSystemRelationTitle',
            render: ({ resolved }) => (
              <EntityRefLinks
                entityRefs={resolved.partOfSystemRelations}
                defaultKind="system"
              />
            ),
          },
          {
            title: 'Family',
            field: 'entity.metadata.annotations.mdsol/domain',
            searchable: true,
            render: ({ entity }) =>
              entity.metadata.annotations?.['mdsol/domain'] && (
                <EntityRefLink
                  entityRef={
                    {
                      kind: 'domain',
                      name: entity.metadata.annotations?.['mdsol/domain'],
                      namespace: entity.metadata.namespace || 'default',
                    } as CompoundEntityRef
                  }
                />
              ),
          },
          CatalogTable.columns.createSpecTypeColumn(),
          CatalogTable.columns.createSpecLifecycleColumn(),
          CatalogTable.columns.createTagsColumn(),
          {
            title: 'Maturity',
            field: 'entity.metadata.not_used',
            render: ({ entity }: Readonly<{ entity: Entity }>) => (
              <MaturityRankWidget entity={entity} chip />
            ),
            searchable: true,
          },
          {
            title: 'Organization',
            field: 'entity.metadata.annotations.mdsol/organization',
            searchable: false,
            hidden: true,
          },
          {
            title: 'Audience',
            field: 'entity.metadata.annotations.mdsol/audience',
            searchable: false,
            hidden: true,
          },
          {
            title: 'Description',
            field: 'entity.metadata.description',
            hidden: true,
          },
          {
            title: 'Runtime',
            field: 'entity.metadata.stages.production.vm.language',
            hidden: true,
          },
          {
            title: 'Runtime Production Version',
            field: 'entity.metadata.stages.production.vm.version',
            hidden: true,
          },
          {
            title: 'Runtime Sandbox Version',
            field: 'entity.metadata.stages.sandbox.vm.version',
            hidden: true,
          },
          {
            title: 'Defined SLOs',
            field: 'entity.metadata.annotations.mdsol/slo-count',
            hidden: true,
          },
          {
            title: 'Telemetry name',
            field: 'entity.metadata.annotations.mdsol/telemetry-name',
            hidden: true,
          },
          {
            title: 'Unavailable relationships',
            field: 'entity.metadata.unknown',
            hidden: true,
            render: ({ entity }: Readonly<{ entity: Entity }>) => (
              <WarningRelationshipsColumn entity={entity} />
            ),
          },
          /** Allows components to be searched by title,
           *  e.g. Ampridatvir (Self Help Service) can be searched by "self help"
           *  Has to be hidden because we already display this column
           */
          {
            title: 'Title',
            field: 'entity.metadata.title',
            hidden: true,
            searchable: true,
          },
        ];
    }
  }, [filters.kind?.value]);

  return (
    <CatalogTable
      columns={columns}
      actions={[]}
      tableOptions={{
        pageSize: 25,
        pageSizeOptions: [25, 100, 600],
        columnsButton: true,
      }}
      title=" "
    />
  );
};

function EntityTypePickerWrapper() {
  // `MedidataKindPicker` handles filtering types for entity kinds other than API or Component.
  // To avoid overlap issues, this will only enable the type picker if API or Component kind is selected.
  const { filters } = useEntityList();
  const showPicker =
    filters.kind?.value === 'api' || filters.kind?.value === 'component';

  return <>{showPicker && <EntityTypePicker />}</>;
}

export function MedidataCatalogPage(props: Readonly<DefaultCatalogPageProps>) {
  // Know more about options for the table: https://material-table.com/#/docs/all-props
  const { initiallySelectedFilter = 'owned', initialKind = 'component' } =
    props;

  return (
    <Page themeId="home">
      <Header title="Medidata Catalog">
        <HeaderWorldClock clockConfigs={clockConfigs} />
      </Header>
      <EntityListProvider>
        <Content>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <MedidataKindPicker initialFilter={initialKind} />
              <UserListPicker initialFilter={initiallySelectedFilter} />
              <EntityTypePickerWrapper />
              <EntityOwnerPicker />
              <EntityLifecyclePicker />
              <EntityTagPicker />
              <EntityProcessingStatusPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <CatalogTableWrapper />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </Content>
      </EntityListProvider>
    </Page>
  );
}
